import { projectStore } from '@core/store';
import RedirectWithHash from '@core/utils/RedirectWithHash';

type SectionContentStatus = Partial<Record<'changelog' | 'guides' | 'recipes' | 'reference', boolean>> | null;

/**
 * Returns the route pathname for the current project's "first page". Use this
 * to redirect users when reaching a navigational dead-end.
 * @example
 * ```ts
 * const history = useHistory();
 *
 * if (noWhereElseToGo) {
 *   history.push(getProjectFirstPagePath());
 * }
 * ```
 */
export function getProjectFirstPagePath(sectionStatus: SectionContentStatus = null) {
  const { first_page: firstPage, links } = projectStore.getState().data.appearance.navigation;
  const pathToContentKey = {
    '/changelog': 'changelog',
    '/docs': 'guides',
    '/recipes': 'recipes',
    '/reference': 'reference',
  } as const;

  const preferredPath = (() => {
    switch (firstPage) {
      case 'documentation':
        return '/docs';
      case 'reference':
        return '/reference';
      case 'landing_page':
        return '/';
      default:
        throw new Error('Missing project navigation first page field');
    }
  })();

  const isPathVisibleAndNonEmpty = (path: string) => {
    if (path === '/') {
      const homeVisibility = links?.home?.visibility;
      return !homeVisibility || homeVisibility === 'enabled';
    }

    const contentKey = pathToContentKey[path as keyof typeof pathToContentKey];
    if (!contentKey) {
      return true;
    }

    const linkVisibility = links?.[contentKey]?.visibility;
    const isVisible = !linkVisibility || linkVisibility === 'enabled';
    const hasContent = !sectionStatus || sectionStatus[contentKey] !== false;

    return isVisible && hasContent;
  };

  if (isPathVisibleAndNonEmpty(preferredPath)) {
    return preferredPath;
  }

  const fallbackPaths = ['/docs', '/reference', '/recipes', '/changelog', '/'];
  const fallbackPath = fallbackPaths.find(isPathVisibleAndNonEmpty);
  return fallbackPath || preferredPath;
}

/**
 * Redirects to the project's "first page" that is currently selected in the
 * `Project > Appearance > Site Navigation` settings. Typically, this is either
 * the landing page, guides section or the reference section.
 *
 * For use only on Hub routes, this is useful when you reach a navigational
 * dead-end with nowhere else to redirect to.
 *
 * @example
 * ```ts
 * // No redirect path exists and there is no last page to return to.
 * if (!redirectPath && !lastPage) {
 *   return <RedirectToProjectFirstPage />;
 * }
 * ```
 */
export default function RedirectToProjectFirstPage() {
  const projectFirstPagePath = getProjectFirstPagePath();

  return <RedirectWithHash to={projectFirstPagePath} />;
}
